import { FC, useMemo } from 'react';

import { Button, Pill, useAdaptive } from '@skillarea/lib-frontend';
import {
  ELeague,
  GameReturnDto,
  getSafeLeagueForGame,
  getStakeCapCentsForLeague,
} from '@skillarea/lib-shared';
import { useTranslation } from 'react-i18next';

import { Container } from '../Container/Container';

import styles from './GameDetail.module.scss';

import { useOnboardStore } from '~/stores/onboardStore';
import { useUserGameStats } from '~/stores/userStore';
import { useIsMaintenanceEnabled } from '~/stores/maintenanceStore';
import { useIsCurrentRouteDraining } from '~/stores/overlapStore';
import { useMinimumAgeGuard } from '~/hooks/useMinimumAgeGuard';
import { useLocalizedGame } from '~/hooks/useLocalizedGame';
import { MinimumAgeModal } from '~/components/MinimumAgeModal/MinimumAgeModal';

const CALIBRATION_GAMES_THRESHOLD = 5;

type TGameDetailProps = GameReturnDto & {
  onDemoPlay?: () => void;
};

export const GameDetail: FC<TGameDetailProps> = (props) => {
  const { id, image, onDemoPlay, slug } = props;
  const { i18n, t } = useTranslation('games');
  const localizedGame = useLocalizedGame(props);
  const { isDesktop } = useAdaptive();
  const isLoggedIn = useOnboardStore((s) => s.isLoggedIn);
  const isMaintenanceEnabled = useIsMaintenanceEnabled();
  const isCurrentRouteDraining = useIsCurrentRouteDraining();
  const gameStats = useUserGameStats();
  const {
    closeMinimumAgeModal,
    isMinimumAgeModalOpen,
    isUnderMinimumAge,
    minimumAge,
    showMinimumAgeModal,
  } = useMinimumAgeGuard();
  const maxStakeDollars = useMemo(() => {
    if (!isLoggedIn) {
      return Math.floor(getStakeCapCentsForLeague(ELeague.IRON) / 100);
    }

    const minLeague = gameStats?.minLeaguePerGame?.[String(id)] ?? ELeague.IRON;
    const gamesPlayedRaw = gameStats?.gamesPerGame?.[id];
    const gamesPlayed =
      typeof gamesPlayedRaw === 'number' && Number.isFinite(gamesPlayedRaw)
        ? gamesPlayedRaw
        : 0;

    const effectiveLeague =
      minLeague === ELeague.IRON && gamesPlayed < CALIBRATION_GAMES_THRESHOLD
        ? ELeague.IRON
        : getSafeLeagueForGame(gameStats, id, 1000);

    return Math.floor(getStakeCapCentsForLeague(effectiveLeague) / 100);
  }, [gameStats, id, isLoggedIn]);

  const titleSection = (
    <div className={styles.titleSection}>
      <h1 className={styles.title}>{localizedGame.name}</h1>

      <div className={styles.entryFee}>
        <Pill icon="coin" size="m">
          {t('ui.maxEntryFee', {
            amount: maxStakeDollars.toLocaleString(i18n.language),
          })}
        </Pill>
      </div>
    </div>
  );

  const renderPlayButton = (className: string, size: 's' | 'm') => {
    if (isMaintenanceEnabled || isCurrentRouteDraining) {
      return (
        <Button
          className={className}
          color="accent"
          data-testid="play-button"
          disabled
          size={size}
          type="button"
          variant="filled"
        >
          {t('ui.play')}
        </Button>
      );
    }

    if (isUnderMinimumAge) {
      return (
        <Button
          className={className}
          color="accent"
          data-testid="play-button"
          onClick={showMinimumAgeModal}
          size={size}
          type="button"
          variant="filled"
        >
          {t('ui.play')}
        </Button>
      );
    }

    return (
      <Button
        as="link"
        className={className}
        color="accent"
        data-testid="play-button"
        hash="play"
        size={size}
        to={`/games/${slug}`}
        variant="filled"
      >
        {t('ui.play')}
      </Button>
    );
  };

  return (
    <Container>
      {!isDesktop && titleSection}
      <div className={styles.gameWrapper}>
        {/* Game Image - on desktop it will be on the right */}
        <div className={styles.imageContainer}>
          <img alt={localizedGame.name} className={styles.image} src={image} />
        </div>

        {/* Game Info - title and entry fee section */}
        <div className={styles.gameInfo}>
          {isDesktop && titleSection}

          {/* Content section with description and button */}
          <div className={styles.contentSection}>
            {/* Description */}
            <div className={styles.description}>
              <p className={styles.descriptionText}>
                {localizedGame.description}
              </p>
            </div>

            {/* Play Button */}
            <div className={styles.buttons}>
              {renderPlayButton(
                styles.playButtonDesktop,
                isDesktop ? 'm' : 's',
              )}
              {onDemoPlay && (
                <Button
                  className={styles.button}
                  color="lavender"
                  onClick={() => {
                    if (isUnderMinimumAge) {
                      showMinimumAgeModal();
                      return;
                    }

                    onDemoPlay();
                  }}
                  size={isDesktop ? 'm' : 's'}
                  variant="outline"
                >
                  {t('ui.demoPlay')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {localizedGame.rules.trim() ? (
        <div className={styles.rulesBelowWrapper}>
          <div className={styles.rules}>
            <h2 className={styles.rulesHeading}>{t('ui.rules')}</h2>
            <p className={styles.rulesText}>{localizedGame.rules}</p>
          </div>
        </div>
      ) : null}

      <MinimumAgeModal
        isOpen={isMinimumAgeModalOpen}
        minimumAge={minimumAge}
        onClose={closeMinimumAgeModal}
      />
    </Container>
  );
};
